import React, { useEffect, useState, type FC } from 'react'
import Info from '../../Ops/fr-pop-up/Info'
import { type OverviewTabProps } from './type'
import { useMutation } from '@apollo/client'
import { AMEND_ASSET_STATUS } from '../../../components/graphql/mutations'
import { amendFRAssetStatus } from '../../Cap-Trader/utils/assetBucketsHelpers'
import { useUserEmail } from '../../../hooks/useUserEmail'
import ActionButton from '../../../components/action-button'
import ShowNotes from '../../../components/show-notes'
import MiniNotepad from '../../../components/notepad'
import Message from '../../../components/message'
import FRTestSection from '../../Ops/fr-pop-up/FundingRequestTest'

const OverviewTab: FC<OverviewTabProps> = ({
  fundingRequestId,
  frSummaryData,
  frTestData,
  notes,
  stage,
  onClose,
  setMessage,
  setMessageColor,
  showTest = false
}) => {
  const [isPrimaryMutating, setIsPrimaryMutating] = useState<boolean>(false)
  const [isSecondaryMutating, setIsSecondaryMutating] = useState<boolean>(false)
  const [noteText, setNoteText] = useState('')
  const [rejectError, setRejectError] = useState('')
  const [amendFRAssetStatusMutation] = useMutation(AMEND_ASSET_STATUS)
  const { userEmail } = useUserEmail()

  const clearRejectError = () => {
    setRejectError('')
  }

  useEffect(() => {
    if (noteText && rejectError) {
      clearRejectError()
    }
  }, [noteText, rejectError])

  if (!frSummaryData) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-32 w-32 border-t-4 border-blue-500"></div>
      </div>
    )
  }

  const { assetTradeDetails, cpTradeDetails, assetCashflows, liabilityCashflows, overall } = frSummaryData
  const { FRCode, ...overallWithoutFRCode } = overall

  const handleClick = async (approvalStatus: string) => {
    if (fundingRequestId) {
      await amendFRAssetStatus({
        amendFRAssetStatusMutation,
        fundingRequestId,
        approvalStatus,
        processRequested: 'CapMarket',
        updatedBy: userEmail ?? '',
        note: noteText,
        setMessage,
        setMessageColor
      })
    }
  }

  const handleRejectClick = async () => {
    if (!noteText.trim()) {
      setRejectError('Please enter a note before rejecting')
      return
    }

    setIsSecondaryMutating(true)
    const approvalStatus = stage === 'Initial' ? 'PROG-COMPL-REJECTED' : 'FULL-PROG-COMPL-REJECTED'
    await handleClick(approvalStatus)
    setIsSecondaryMutating(false)
    onClose?.()
  }

  const handleProceedClick = async () => {
    setIsPrimaryMutating(true)
    const approvalStatus = stage === 'Initial' ? 'PROG-COMPL-APPROVED' : 'FULL-PROG-COMPL-APPROVED'
    await handleClick(approvalStatus)
    setIsPrimaryMutating(false)
    onClose?.()
  }

  return (
    <div className="p-6 bg-white">
      <div className="mb-4 flex items-center gap-2">
        <h3 className="text-xl font-bold text-gray-900">Funding Request Overview:</h3>
        <span className="text-l text-gray-700 bg-yellow-100 p-1">{overall.FRCode}</span>
      </div>
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Info data={[assetTradeDetails]} title="Asset Trade Details (TRS)" specialTRS={true} />
          <Info data={[cpTradeDetails]} title="CP Trade Details" />
          <Info data={[assetCashflows]} title="Asset Cash Flows" />
          <Info data={[liabilityCashflows]} title="Liability Cash Flows" />
          <Info data={[overallWithoutFRCode]} title="Summary" />
        </div>
        {showTest && frTestData && (
          <div>
            <FRTestSection data={frTestData} />
          </div>
        )}
        <div className="flex flex-col gap-2">
          <ShowNotes data={notes} defaultExpand={true} />
          <MiniNotepad noteText={noteText} setNoteText={setNoteText} styleClass="w-full h-full" />
        </div>
      </div>

      <div className="mt-6 flex justify-end space-x-4">
        {rejectError && (
          <div className="mt-2">
            <Message
              message={rejectError}
              color="red"
              clearMessage={clearRejectError}
              className="p-2 rounded"
            />
          </div>
        )}
        {!(stage === 'read-only') && (
          <ActionButton
            primaryLabel="Approve"
            secondaryLabel="Reject"
            primaryAction={handleProceedClick}
            secondaryAction={handleRejectClick}
            primaryLoading={isPrimaryMutating}
            secondaryLoading={isSecondaryMutating}
            primaryDisabled={isPrimaryMutating || isSecondaryMutating}
            secondaryDisabled={isPrimaryMutating || isSecondaryMutating}
          />
        )}
      </div>
    </div>
  )
}

export default React.memo(OverviewTab)
